import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * I18nDebugOverlay — Détecte visuellement les textes non-traduits.
 *
 * Activation :
 *   1. localStorage.setItem('i18n_debug', '1') puis F5
 *   2. OU URL avec ?i18n=debug
 *   3. OU touche clavier Ctrl+Shift+I (toggle)
 *
 * Ce qui est détecté (uniquement quand la langue courante n'est PAS 'fr') :
 *   - Texte contenant au moins une lettre accentuée typiquement FR (é/è/à/ê/ç/î/ô/û)
 *   - Longueur >= 4 caractères
 *   - Élément visible (pas display:none / visibility:hidden / opacity:0)
 *
 * Effet : outline rouge fluo + tooltip natif indiquant qu'il s'agit d'un
 * texte hardcodé français à traduire. Affiche aussi un badge flottant
 * avec le compteur total (clic → console.table des occurrences).
 *
 * Aucun impact en production : le composant ne s'auto-active JAMAIS
 * sans une action explicite du développeur (localStorage / URL / raccourci).
 */

const STORAGE_KEY = 'i18n_debug';
const FR_ACCENTS_RE = /[éèêëàâäîïôöùûüç]/i;
const MIN_LEN = 4;
const HIGHLIGHT_CLASS = 'i18n-debug-flagged';

// Injecté une seule fois : outline + fond fluo semi-transparent
const CSS = `
  .${HIGHLIGHT_CLASS} {
    outline: 2px dashed #ef4444 !important;
    outline-offset: 2px !important;
    background: rgba(239, 68, 68, 0.15) !important;
    border-radius: 3px;
  }
  #i18n-debug-badge {
    position: fixed;
    bottom: 16px;
    right: 16px;
    z-index: 99999;
    background: #ef4444;
    color: white;
    padding: 8px 14px;
    border-radius: 999px;
    font-family: system-ui, -apple-system, sans-serif;
    font-size: 12px;
    font-weight: bold;
    box-shadow: 0 4px 12px rgba(0,0,0,0.25);
    cursor: pointer;
    user-select: none;
  }
  #i18n-debug-badge:hover { background: #dc2626; }
`;

function isReallyVisible(el) {
  if (!el) return false;
  const cs = window.getComputedStyle(el);
  if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) === 0) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

const IGNORED_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'CODE', 'PRE']);

const I18nDebugOverlay = () => {
  const { language } = useLanguage();
  const [enabled, setEnabled] = useState(false);
  const [count, setCount] = useState(0);
  const flaggedRef = useRef([]);

  // Détection activation (localStorage / URL / raccourci)
  useEffect(() => {
    const check = () => {
      try {
        const fromLs = localStorage.getItem(STORAGE_KEY) === '1';
        const params = new URLSearchParams(window.location.search);
        const fromUrl = params.get('i18n') === 'debug';
        return fromLs || fromUrl;
      } catch {
        return false;
      }
    };
    setEnabled(check());
    const onKey = (e) => {
      // Ctrl+Shift+I : toggle
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'i') {
        e.preventDefault();
        const next = !enabled;
        try { localStorage.setItem(STORAGE_KEY, next ? '1' : '0'); } catch { /* noop */ }
        setEnabled(next);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled]);

  // Injection CSS (une seule fois)
  useEffect(() => {
    if (!enabled) return undefined;
    const style = document.createElement('style');
    style.id = 'i18n-debug-style';
    style.textContent = CSS;
    document.head.appendChild(style);
    return () => { style.remove(); };
  }, [enabled]);

  const clearFlags = useCallback(() => {
    flaggedRef.current.forEach((el) => {
      try { el.classList.remove(HIGHLIGHT_CLASS); el.removeAttribute('data-i18n-flag'); } catch { /* noop */ }
    });
    flaggedRef.current = [];
  }, []);

  const scan = useCallback(() => {
    if (!enabled || language === 'fr') {
      clearFlags();
      setCount(0);
      return;
    }
    clearFlags();
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        const parent = node.parentElement;
        if (!parent) return NodeFilter.FILTER_REJECT;
        if (IGNORED_TAGS.has(parent.tagName)) return NodeFilter.FILTER_REJECT;
        // Skip nos propres badges de debug
        if (parent.closest('#i18n-debug-badge')) return NodeFilter.FILTER_REJECT;
        // Skip les éléments avec `data-i18n-ignore`
        if (parent.closest('[data-i18n-ignore]')) return NodeFilter.FILTER_REJECT;
        const text = (node.nodeValue || '').trim();
        if (text.length < MIN_LEN) return NodeFilter.FILTER_REJECT;
        if (!FR_ACCENTS_RE.test(text)) return NodeFilter.FILTER_REJECT;
        if (!isReallyVisible(parent)) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      },
    });
    const found = [];
    let n = walker.nextNode();
    while (n) {
      const parent = n.parentElement;
      if (parent && !parent.classList.contains(HIGHLIGHT_CLASS)) {
        parent.classList.add(HIGHLIGHT_CLASS);
        parent.setAttribute('data-i18n-flag', (n.nodeValue || '').trim().slice(0, 80));
        parent.title = `⚠ Texte français hardcodé (langue courante: ${language.toUpperCase()}) : « ${(n.nodeValue || '').trim().slice(0, 120)} »`;
        found.push(parent);
      }
      n = walker.nextNode();
    }
    flaggedRef.current = found;
    setCount(found.length);
  }, [enabled, language, clearFlags]);

  // Re-scan périodique (2s) + MutationObserver pour re-scan sur changements DOM
  useEffect(() => {
    if (!enabled) return undefined;
    scan();
    const iv = setInterval(scan, 2000);
    const mo = new MutationObserver(() => {
      // Debounce : reschedule au prochain tick
      window.clearTimeout(mo._t);
      mo._t = window.setTimeout(scan, 400);
    });
    mo.observe(document.body, { childList: true, subtree: true, characterData: true });
    return () => {
      clearInterval(iv);
      mo.disconnect();
      clearFlags();
    };
  }, [enabled, scan, clearFlags]);

  const handleBadgeClick = () => {
    if (flaggedRef.current.length === 0) {
      console.log('[i18n-debug] Aucun texte hardcodé détecté ✅');
      return;
    }
    const table = flaggedRef.current.map((el, i) => ({
      '#': i + 1,
      text: (el.getAttribute('data-i18n-flag') || '').slice(0, 60),
      tag: el.tagName,
      selector: el.className?.split(' ').slice(0, 2).join('.') || '(none)',
    }));
    console.groupCollapsed(`🔍 i18n-debug — ${table.length} textes hardcodés détectés`);
    console.table(table);
    console.log('Éléments dans flaggedRef :', flaggedRef.current);
    console.groupEnd();
  };

  const disable = () => {
    try { localStorage.setItem(STORAGE_KEY, '0'); } catch { /* noop */ }
    setEnabled(false);
    clearFlags();
    setCount(0);
  };

  if (!enabled || language === 'fr') return null;

  return (
    <div
      id="i18n-debug-badge"
      data-i18n-ignore="true"
      data-testid="i18n-debug-badge"
      onClick={handleBadgeClick}
      title="Clic : console.table des occurrences · Ctrl+Shift+I : toggle · double-clic : désactiver"
      onDoubleClick={disable}
    >
      🔍 {count} texte{count !== 1 ? 's' : ''} FR non-traduit{count !== 1 ? 's' : ''}
    </div>
  );
};

export default I18nDebugOverlay;
